import { useState, useMemo, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFilters } from '../useFilters';
import Calendars from 'libs/api/admin/calendars';
import moment from 'moment';
import { DatePicker, Input, Select, Typography } from 'antd';
import CalendarsEditAdapter from './edit-calendar.adapter';

const api = new Calendars();

const getDates = (date, groupBy) => {
  return {
    by_date_from: moment(date).startOf(groupBy).format('YYYY-MM-DD'),
    by_date_to: moment(date).endOf(groupBy).format('YYYY-MM-DD'),
  };
};
const ErrorView = ({ errors, errorKey }) => {
  if (!errors) {
    return null;
  }

  if (!errors[errorKey]) {
    return null;
  }

  return <Typography.Text type='danger'>{errors[errorKey]}</Typography.Text>;
};

export const useEditCalendar = () => {
  const { t } = useTranslation();
  const { id: date } = useParams();
  const [loading, setLoading] = useState(true);
  const [singleData, setSingleData] = useState([]);
  const [errors, setErrors] = useState(null);
  const { groupBy, backendFilters } = useFilters();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const {
        data: { calendars },
      } = await api.list({
        params: {
          ...getDates(date, groupBy),
        },
      });
      setSingleData(CalendarsEditAdapter(calendars || []));
    } catch (e) {
    } finally {
      setLoading(false);
    }
  }, [date, groupBy]);

  useEffect(() => {
    (async () => {
      await fetchData();
    })();
  }, [fetchData]);

  const onChangeField = useCallback(
    (row) => {
      return (e) => {
        const {
          target: { name, value },
        } = e;
        if (errors) {
          setErrors(null);
        }
        setSingleData((s) => {
          return s.map((i) => {
            if (i.id === row.id) {
              return {
                ...i,
                [name]: value,
              };
            }
            return i;
          });
        });
      };
    },
    [errors],
  );

  const onChangeSelect = useCallback(
    (row, name) => {
      return (value) => {
        if (errors) {
          setErrors(null);
        }
        setSingleData((s) => {
          return s.map((i) => {
            if (i.id === row.id) {
              return {
                ...i,
                [name]: value,
              };
            }
            return i;
          });
        });
      };
    },
    [errors],
  );

  const onChangeDatePicker = useCallback(
    (row, name) => {
      return (date) => {
        if (errors) {
          setErrors(null);
        }
        setSingleData((s) => {
          return s.map((i) => {
            if (i.id === row.id) {
              return {
                ...i,
                [name]: date,
              };
            }
            return i;
          });
        });
      };
    },
    [errors],
  );

  const renderInput = useCallback(
    (name) => {
      return (data, row) => {
        const rowErrors = errors && errors[row.id] && errors[row.id][0];
        return (
          <>
            <Input onChange={onChangeField(row)} name={name} value={data} />
            <ErrorView errorKey={name} errors={rowErrors} />
          </>
        );
      };
    },
    [onChangeField, errors],
  );

  const by_type_close = useMemo(
    () => backendFilters?.type_closes || [],
    [backendFilters],
  );
  const day_types = useMemo(
    () => backendFilters?.day_types || [],
    [backendFilters],
  );
  const by_calendar_categories = useMemo(
    () => backendFilters?.by_calendar_categories || [],
    [backendFilters],
  );

  const columns = useMemo(() => {
    return [
      {
        title: t('Settimana'),
        dataIndex: 'week_day',
        width: 130,
        render: (data) => `${t('Settimana')} ${data}`,
      },
      {
        title: t('Data'),
        dataIndex: 'calendar_date',
        width: 200,
        render: (data, row) => {
          const rowErrors = errors && errors[row.id] && errors[row.id][0];
          return (
            <>
              <DatePicker
                format={'DD MMMM YYYY'}
                defaultValue={moment(data)}
                style={{ width: '100%' }}
                onChange={onChangeDatePicker(row, 'calendar_date')}
              />
              <ErrorView errors={rowErrors} errorKey='calendar_date' />
            </>
          );
        },
      },
      {
        title: t('Giorno'),
        dataIndex: 'day',
        width: 130,
        render: (data) => (
          <span style={{ textTransform: 'capitalize' }}>{data}</span>
        ),
      },
      {
        title: t('Aperture'),
        dataIndex: 'type_close',
        width: 200,
        render: (data, row) => {
          const rowErrors = errors && errors[row.id] && errors[row.id][0];
          return (
            <>
              <Select
                name='type_close'
                value={data}
                style={{ width: '100%' }}
                onChange={onChangeSelect(row, 'type_close')}
              >
                {by_type_close?.map((i) => (
                  <Select.Option key={i.value} value={i.value}>
                    {i.title}
                  </Select.Option>
                ))}
              </Select>
              <ErrorView errors={rowErrors} errorKey='type_close' />
            </>
          );
        },
      },
      {
        title: t('Tipo Giorno'),
        dataIndex: 'day_type',
        width: 200,
        render: (data, row) => {
          const rowErrors = errors && errors[row.id] && errors[row.id][0];
          return (
            <>
              <Select
                name='day_type'
                value={data}
                style={{ width: '100%' }}
                onChange={onChangeSelect(row, 'day_type')}
              >
                {day_types?.map((i) => (
                  <Select.Option key={i.value} value={i.value}>
                    {i.title}
                  </Select.Option>
                ))}
              </Select>
              <ErrorView errors={rowErrors} errorKey='day_type' />
            </>
          );
        },
      },
      {
        title: t('Footfall'),
        dataIndex: 'footfall',
        width: 200,
        render: renderInput('footfall'),
      },
      {
        title: t('Trading calendar'),
        width: 200,
        dataIndex: 'analytics_calendar_category_id',
        // render: renderInput('analytics_calendar_category'),
        render: (data, row) => {
          const rowErrors = errors && errors[row.id] && errors[row.id][0];
          return (
            <>
              <Select
                name='analytics_calendar_category_id'
                value={data}
                style={{ width: '100%' }}
                onChange={onChangeSelect(row, 'analytics_calendar_category_id')}
              >
                <Select.Option value={null}>
                  {t('Trading calendar')}
                </Select.Option>
                {by_calendar_categories?.map((i) => (
                  <Select.Option key={i.value} value={i.value}>
                    {i.title}
                  </Select.Option>
                ))}
              </Select>
              <ErrorView
                errors={rowErrors}
                errorKey='analytics_calendar_category'
              />
            </>
          );
        },
      },
      {
        title: t('Note'),
        dataIndex: 'comment',
        width: 200,
        render: renderInput('comment'),
      },
    ];
  }, [
    t,
    by_type_close,
    day_types,
    renderInput,
    errors,
    onChangeDatePicker,
    onChangeSelect,
    by_calendar_categories,
  ]);

  const onSubmit = async () => {
    setLoading(true);
    try {
      await api.put('list_update', {
        data: {
          analytics_calendars: singleData,
        },
      });
      fetchData();
    } catch (e) {
      setErrors(e?.response?.data);
    } finally {
      setLoading(false);
    }
  };

  return {
    singleData,
    columns,
    loading,
    onSubmit,
    errors,
  };
};
